/**
 * The two shapes a list payload states its lead counts in. They are the same three buckets under
 * two sets of names, because they come from two different queries:
 *
 * - `found` / `savedForLater` / `assignedLeads` — the **plural** lean query behind the Lead List
 *   Overview rows (`list-query.repository.ts`).
 * - `draftCount` / `savedForLaterCount` / `assignedCount` — the **single** lean list
 *   (`GET list/:id/lean` → `fetchLeanListCounts`), which every list-detail surface holds.
 *
 * A payload carries one set or the other, never both, so the predicate below reads whichever is
 * present. (The *full* list, `GET list/:id`, carries neither — it is built with `undefined` counts
 * — which is why lead presence is passed alongside it rather than read off it; see
 * {@link getListSourceState}.)
 */
export interface ListLeadCounts {
	found?: number | null;
	savedForLater?: number | null;
	assignedLeads?: number | null;
	draftCount?: number | null;
	savedForLaterCount?: number | null;
	assignedCount?: number | null;
}

/**
 * Whether a list already has at least one lead on it.
 *
 * The counts cover every lead a user can act on in the Unassigned view — including leads moved in
 * from another list (they land as DRAFT → `found`/`draftCount`) onto a list that has no source of
 * its own.
 *
 * Each count may arrive as a string at runtime (SQL aggregate), so they are coerced before summing
 * (a raw `+` would concatenate — see web CLAUDE.md "API numbers arrive as strings").
 */
const toCount = (value?: number | null): number => Number(value ?? 0) || 0;

export const listHasLeads = (list: ListLeadCounts | null | undefined): boolean => {
	if (!list) {
		return false;
	}

	return (
		toCount(list.found) +
			toCount(list.savedForLater) +
			toCount(list.assignedLeads) +
			toCount(list.draftCount) +
			toCount(list.savedForLaterCount) +
			toCount(list.assignedCount) >
		0
	);
};
